import type { ShaderDataTypeKeys, Nullable } from "models-webgl2";
import { SHADER_DATA_TYPE_SIZE } from "../webgl2.constants";

export class BufferElement {
  name: string;
  type: ShaderDataTypeKeys;
  size: number;
  offset: number;
  normalized: boolean;

  constructor(name: string, type: ShaderDataTypeKeys, normalized = false) {
    this.name = name;
    this.type = type;
    this.size = SHADER_DATA_TYPE_SIZE[type];
    this.offset = 0;
    this.normalized = normalized;
  }
}

export class BufferLayout {
  elements: BufferElement[];
  stride = 0;

  constructor(elements: BufferElement[]) {
    this.elements = elements;

    this.calcOffsetAndStride();
  }

  private calcOffsetAndStride() {
    let offset = 0;

    this.elements.forEach((elem) => {
      elem.offset = offset;
      offset += elem.size;
      this.stride += elem.size;
    });
  }
}

export class VertexBuffer {
  gl: WebGL2RenderingContext;
  buffer: WebGLBuffer;
  data: ArrayBufferLike;
  layout: BufferLayout;
  drawType: number;

  constructor(
    gl: WebGL2RenderingContext,
    data: ArrayBufferLike,
    layout: BufferLayout,
    drawType?: Nullable<number>,
  ) {
    this.gl = gl;
    this.buffer = this.gl.createBuffer() as WebGLBuffer;
    this.data = data;
    this.layout = layout;
    this.drawType = drawType ?? this.gl.STATIC_DRAW;

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, this.data, this.drawType);
  }

  bind() {
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffer);
  }

  unbind() {
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);
  }
}

export class IndexBuffer {
  gl: WebGL2RenderingContext;
  buffer: WebGLBuffer;

  constructor(gl: WebGL2RenderingContext, array: number[]) {
    this.gl = gl;
    this.buffer = this.gl.createBuffer() as WebGLBuffer;

    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.buffer);
    this.gl.bufferData(this.gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(array), this.gl.STATIC_DRAW);
  }

  bind() {
    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.buffer);
  }

  unbind() {
    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, null);
  }
}
